import {
  EVIDENCE_CHIP_HOVER_CLOSE_DELAY_MS,
  isInsideHoverCardTrigger,
  shouldAllowContentKeepAliveOnTriggerLeave,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/evidenceChipHoverOpen'
import React, { useEffect, useRef, useState } from 'react'

function clearTimer(timerRef: React.MutableRefObject<number | null>): void {
  if (timerRef.current === null) return
  window.clearTimeout(timerRef.current)
  timerRef.current = null
}

export function useEvidenceChipHoverOpen(
  onOpenChange?: (open: boolean) => void
): {
  open: boolean
  handleOpenChange: (nextOpen: boolean) => void
  handleTriggerPointerEnter: () => void
  handleTriggerPointerLeave: (event: React.PointerEvent<HTMLElement>) => void
  handleContentPointerEnter: () => void
  handleContentPointerLeave: (event: React.PointerEvent<HTMLElement>) => void
} {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const triggerHoveredRef = useRef(false)
  const contentKeepAliveRef = useRef(false)
  const closeTimerRef = useRef<number | null>(null)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  useEffect(() => {
    return () => {
      clearTimer(closeTimerRef)
    }
  }, [])

  const setOpenAndNotify = (nextOpen: boolean): void => {
    openRef.current = nextOpen
    setOpen(nextOpen)
    onOpenChangeRef.current?.(nextOpen)
  }

  const scheduleClose = (): void => {
    clearTimer(closeTimerRef)
    closeTimerRef.current = window.setTimeout(() => {
      contentKeepAliveRef.current = false
      setOpenAndNotify(false)
    }, EVIDENCE_CHIP_HOVER_CLOSE_DELAY_MS)
  }

  const handleOpenChange = (nextOpen: boolean): void => {
    // Radix content also calls onOpen on pointerenter (including during exit
    // Presence). Reject opens that are not from the chip or an intentional
    // chip→card transfer.
    if (nextOpen) {
      const allowOpen =
        triggerHoveredRef.current ||
        contentKeepAliveRef.current ||
        openRef.current
      if (!allowOpen) return
      clearTimer(closeTimerRef)
      contentKeepAliveRef.current = true
      setOpenAndNotify(true)
      return
    }
    contentKeepAliveRef.current = false
    clearTimer(closeTimerRef)
    setOpenAndNotify(false)
  }

  const handleTriggerPointerEnter = (): void => {
    triggerHoveredRef.current = true
  }

  const handleTriggerPointerLeave = (
    event: React.PointerEvent<HTMLElement>
  ): void => {
    triggerHoveredRef.current = false
    const allowKeepAlive = shouldAllowContentKeepAliveOnTriggerLeave({
      relatedTarget: event.relatedTarget,
      clientX: event.clientX,
      clientY: event.clientY,
      triggerRect: event.currentTarget.getBoundingClientRect(),
    })
    if (allowKeepAlive) {
      contentKeepAliveRef.current = true
      return
    }
    contentKeepAliveRef.current = false
    scheduleClose()
  }

  const handleContentPointerEnter = (): void => {
    if (!contentKeepAliveRef.current) {
      clearTimer(closeTimerRef)
      setOpenAndNotify(false)
      return
    }
    clearTimer(closeTimerRef)
  }

  const handleContentPointerLeave = (
    event: React.PointerEvent<HTMLElement>
  ): void => {
    if (isInsideHoverCardTrigger(event.relatedTarget)) return
    contentKeepAliveRef.current = false
    scheduleClose()
  }

  return {
    open,
    handleOpenChange,
    handleTriggerPointerEnter,
    handleTriggerPointerLeave,
    handleContentPointerEnter,
    handleContentPointerLeave,
  }
}
